import {
    computed,
    ref,
} from '@vue/composition-api';
import {
    SdsTooltipDirection,
    SdsTooltipVariant,
} from '@superbet-group/web.lib.ui-components';

import { TooltipDefinition } from './types';

let tooltipManager: ReturnType<typeof createTooltipManager> | null = null;

export function useTooltipManager() {
    if (tooltipManager == null) {
        tooltipManager = createTooltipManager();
    }

    return tooltipManager;
}

function createTooltipManager() {
    const tooltipPaddingPx = 8;
    const minTooltipSpaceInAutoModePx = 160 + tooltipPaddingPx;

    const tooltipRef = ref<TooltipDefinition | null>(null);
    const elementRef = ref<HTMLElement | null>(null);
    const updateCounter = ref(0);

    const hotkey = computed<string | undefined>(() => {
        return tooltipRef.value?.hotkeys;
    });

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const tooltipProps = computed<TooltipProps | undefined>(() => {
        // Just to make the whole thing reactive, it's a hack
        updateCounter.value;

        if (tooltipRef.value == null || elementRef.value == null) {
            return;
        }

        const orientation = tooltipRef.value.orientation;

        const rect = elementRef.value.getBoundingClientRect();
        const topDistance = rect.top;
        const bottomDistance = window.innerHeight - rect.bottom;
        const leftDistance = rect.left;
        const rightDistance = window.innerWidth - rect.right;

        const isAbove = orientation === 'top'
            || (orientation === 'vertical' && topDistance > bottomDistance)
            || (orientation === 'auto' && rightDistance >= minTooltipSpaceInAutoModePx);

        const isBelow = orientation === 'bottom'
            || (orientation === 'vertical' && topDistance <= bottomDistance)
            || (orientation === 'auto' && rightDistance >= minTooltipSpaceInAutoModePx);

        const isLeft = orientation === 'left'
            || (orientation === 'horizontal' && leftDistance > rightDistance)
            || (orientation === 'auto' && leftDistance >= minTooltipSpaceInAutoModePx);

        if (isAbove) {
            return {
                variant: tooltipRef.value.variant,
                direction: 'down' as SdsTooltipDirection,
                text: tooltipRef.value.text,
                isHtml: tooltipRef.value.isHtml,
                isRaw: tooltipRef.value.isRaw,
                bottom: window.innerHeight - rect.bottom + rect.height + tooltipPaddingPx,
                left: rect.left + rect.width / 2,
                hotkey: hotkey.value,
                transform: 'translateX(-50%)',
            };
        } else if (isBelow) {
            return {
                variant: tooltipRef.value.variant,
                direction: 'up' as SdsTooltipDirection,
                text: tooltipRef.value.text,
                isHtml: tooltipRef.value.isHtml,
                isRaw: tooltipRef.value.isRaw,
                top: rect.bottom + tooltipPaddingPx,
                left: rect.left + rect.width / 2,
                hotkey: hotkey.value,
                transform: 'translateX(-50%)',
            };
        } else if (isLeft) {
            return {
                variant: tooltipRef.value.variant,
                direction: 'right' as SdsTooltipDirection,
                text: tooltipRef.value.text,
                isHtml: tooltipRef.value.isHtml,
                isRaw: tooltipRef.value.isRaw,
                left: rect.left - tooltipPaddingPx,
                top: rect.top + rect.height / 2,
                hotkey: hotkey.value,
                transform: 'translate(-100%, -50%)',
            };
        } else {
            return {
                variant: tooltipRef.value.variant,
                direction: 'left' as SdsTooltipDirection,
                text: tooltipRef.value.text,
                isHtml: tooltipRef.value.isHtml,
                isRaw: tooltipRef.value.isRaw,
                left: rect.right + tooltipPaddingPx,
                top: rect.top + rect.height / 2,
                hotkey: hotkey.value,
                transform: 'translateY(-50%)',
            };
        }
    });

    const observer = new IntersectionObserver(onIntersection, {
        root: null,
    });

    function onIntersection(entries: IntersectionObserverEntry[]) {
        const entry = entries[0];

        if (entry.isIntersecting) {
            updateCounter.value += 1; // Force re-render
        }
    }

    function setActiveTooltip(tooltip: TooltipDefinition, rootElement: HTMLElement) {
        clearActiveTooltip();

        tooltipRef.value = tooltip;
        elementRef.value = rootElement;

        observer.observe(rootElement);
    }

    function clearActiveTooltip() {
        if (elementRef.value) {
            observer.unobserve(elementRef.value);
        }

        tooltipRef.value = null;
        elementRef.value = null;
    }

    function toggleActiveTooltip(tooltip: TooltipDefinition, rootElement: HTMLElement) {
        if (tooltipRef.value == null || elementRef.value !== rootElement) {
            setActiveTooltip(tooltip, rootElement);
        } else {
            clearActiveTooltip();
        }
    }

    return {
        tooltipProps,
        setActiveTooltip,
        toggleActiveTooltip,
        clearActiveTooltip,
    };
}

type TooltipProps = {
    variant: SdsTooltipVariant;
    direction: SdsTooltipDirection;
    text: string;
    isHtml?: boolean;
    isRaw?: boolean;
    hotkey?: string;
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    transform: string;
};
